import { CloudSlash } from "@phosphor-icons/react";
import { lazy, Suspense, useEffect } from "react";
import { AccountMenu } from "./components/auth/AccountMenu";
import { GuestLibraryImportBanner } from "./components/auth/GuestLibraryImportBanner";
import { SiteSearch } from "./components/search/SiteSearch";
import { useAuth } from "./lib/auth/AuthContext";
import { useOnlineStatus } from "./lib/hooks/useOnlineStatus";
import { getFooterRoutes, getNavRoutes, resolveRoute } from "./routes";

const SpotMapPage = lazy(() => import("./pages/SpotMapPage").then((module) => ({ default: module.SpotMapPage })));
const WeatherPage = lazy(() => import("./pages/WeatherPage").then((module) => ({ default: module.WeatherPage })));
const IssueReportPage = lazy(() => import("./pages/IssueReportPage").then((module) => ({ default: module.IssueReportPage })));
const ResourcesPage = lazy(() =>
  import("./pages/ResourcesPage").then((module) => ({ default: module.ResourcesPage }))
);
const GearPage = lazy(() => import("./pages/GearPage").then((module) => ({ default: module.GearPage })));
const CommunityPage = lazy(() =>
  import("./pages/CommunityPage").then((module) => ({ default: module.CommunityPage }))
);
const LoginPage = lazy(() => import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() =>
  import("./pages/RegisterPage").then((module) => ({ default: module.RegisterPage }))
);
const RecoveryPage = lazy(() =>
  import("./pages/RecoveryPage").then((module) => ({ default: module.RecoveryPage }))
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage }))
);
const AdminPage = lazy(() => import("./pages/AdminPage").then((module) => ({ default: module.AdminPage })));
const AdminPhotoPage = lazy(() => import("./pages/AdminPhotoPage").then((module) => ({ default: module.AdminPhotoPage })));
const AdminReportsPage = lazy(() => import("./pages/AdminReportsPage").then((module) => ({ default: module.AdminReportsPage })));
const SpotDetailPage = lazy(() =>
  import("./pages/SpotDetailPage").then((module) => ({ default: module.SpotDetailPage }))
);
const SpotSubmissionPage = lazy(() =>
  import("./pages/SpotSubmissionPage").then((module) => ({ default: module.SpotSubmissionPage }))
);
const ResourceDetailPage = lazy(() =>
  import("./pages/ResourceDetailPage").then((module) => ({ default: module.ResourceDetailPage }))
);
const GearCategoryPage = lazy(() =>
  import("./pages/GearCategoryPage").then((module) => ({ default: module.GearCategoryPage }))
);
const CommunityPostPage = lazy(() =>
  import("./pages/CommunityPostPage").then((module) => ({ default: module.CommunityPostPage }))
);
const ProfilePage = lazy(() =>
  import("./pages/ProfilePage").then((module) => ({ default: module.ProfilePage }))
);
const PlaceholderPage = lazy(() =>
  import("./pages/PlaceholderPage").then((module) => ({ default: module.PlaceholderPage }))
);

function App() {
  const isOnline = useOnlineStatus();
  const resolvedRoute = resolveRoute(window.location.pathname);
  const activePath = resolvedRoute.type === "spot-detail"
    ? "/"
    : resolvedRoute.route.id === "spot-submit"
      ? "/"
    : resolvedRoute.type === "resource-detail"
      ? "/resources"
      : resolvedRoute.type === "gear-category"
        ? "/gear"
        : resolvedRoute.type === "community-post"
          ? "/community"
          : resolvedRoute.route.path || window.location.pathname;

  return (
    <div className="app-shell">
      <SiteHeader activePath={activePath} />
      {!isOnline ? (
        <div className="offline-banner" role="status">
          <CloudSlash size={18} weight="duotone" aria-hidden="true" />
          <span>Offline. Cached pages and local saves remain available; reports will not be submitted.</span>
        </div>
      ) : null}
      <GuestLibraryImportBanner />
      <Suspense fallback={<RouteFallback />}>
        {resolvedRoute.route.id === "spot-map" ? (
          <SpotMapPage />
        ) : resolvedRoute.route.id === "weather" ? (
          <WeatherPage />
        ) : resolvedRoute.route.id === "report" ? (
          <IssueReportPage />
        ) : resolvedRoute.route.id === "resources" ? (
          <ResourcesPage />
        ) : resolvedRoute.route.id === "gear" ? (
          <GearPage />
        ) : resolvedRoute.route.id === "community" ? (
          <CommunityPage />
        ) : resolvedRoute.route.id === "login" ? (
          <LoginPage />
        ) : resolvedRoute.route.id === "register" ? (
          <RegisterPage />
        ) : resolvedRoute.route.id === "recover" ? (
          <RecoveryPage />
        ) : resolvedRoute.route.id === "settings" ? (
          <SettingsPage />
        ) : resolvedRoute.route.id === "admin" ? (
          <AdminRoute page="spots" />
        ) : resolvedRoute.route.id === "admin-photos" ? (
          <AdminRoute page="photos" />
        ) : resolvedRoute.route.id === "admin-reports" ? (
          <AdminRoute page="reports" />
        ) : resolvedRoute.route.id === "spot-submit" ? (
          <SpotSubmissionPage />
        ) : resolvedRoute.type === "spot-detail" ? (
          <SpotDetailPage slug={resolvedRoute.params.slug ?? ""} />
        ) : resolvedRoute.type === "resource-detail" ? (
          <ResourceDetailPage slug={resolvedRoute.params.slug ?? ""} />
        ) : resolvedRoute.type === "gear-category" ? (
          <GearCategoryPage slug={resolvedRoute.params.category ?? ""} />
        ) : resolvedRoute.type === "community-post" ? (
          <CommunityPostPage slug={resolvedRoute.params.slug ?? ""} />
        ) : resolvedRoute.type === "username" ? (
          <ProfilePage username={resolvedRoute.params.username ?? ""} />
        ) : (
          <PlaceholderPage route={resolvedRoute.route} params={resolvedRoute.params} />
        )}
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function AdminRoute({ page }: { page: "spots" | "photos" | "reports" }) {
  const { loading, profile } = useAuth();
  const isAdmin = profile?.role === "admin";

  useEffect(() => {
    if (!loading && !isAdmin) {
      window.location.replace("/");
    }
  }, [isAdmin, loading]);

  if (loading || !isAdmin) {
    return <RouteFallback />;
  }

  if (page === "photos") {
    return <AdminPhotoPage />;
  }
  return page === "reports" ? <AdminReportsPage /> : <AdminPage />;
}

function RouteFallback() {
  return (
    <main className="app-main route-loading" role="status" aria-label="Loading page">
      <div className="route-loading-heading" />
      <div className="route-loading-layout">
        <div />
        <div />
        <div />
      </div>
    </main>
  );
}

function SiteHeader({ activePath }: { activePath: string }) {
  const navRoutes = getNavRoutes();

  return (
    <header className="site-header">
      <a className="brand-lockup" href="/" aria-label="Dahlini home">
        <img src="/assets/logos/iconlogo-v1.png" alt="" className="brand-icon" />
        <img src="/assets/logos/typelogo-v1.png" alt="Dahlini" className="brand-type" />
      </a>

      <nav className="top-nav" aria-label="Primary">
        {navRoutes.map((route) => (
          <a className={route.path === activePath ? "active" : undefined} href={route.path} key={route.id}>
            {route.label}
          </a>
        ))}
      </nav>

      <div className="header-tools">
        <SiteSearch />
        <AccountMenu />
      </div>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <div>
        <strong>Dahlini</strong>
        <span>Fishing spots, field conditions, practical guides, and gear planning.</span>
      </div>
      <nav aria-label="Footer">
        {getFooterRoutes().map((route) => (
          <a href={route.path} key={route.id}>
            {route.label}
          </a>
        ))}
      </nav>
    </footer>
  );
}

export default App;
